import dayjs from 'dayjs';
import { getStatusTextByNum } from '@/enums/PlanStatusEnum';

// Значения обязаны совпадать с App\Enums\CRM\Send\CrmPlanSendState на бекенде.
export const SEND_STATE = Object.freeze({
  NOT_CONFIGURED: 'not_configured',
  NOT_SENT: 'not_sent',
  PARTIALLY_SENT: 'partially_sent',
  SENT: 'sent',
});

const STATE_COLORS = Object.freeze({
  [SEND_STATE.NOT_CONFIGURED]: 'default',
  [SEND_STATE.NOT_SENT]: 'error',
  [SEND_STATE.PARTIALLY_SENT]: 'orange',
  [SEND_STATE.SENT]: 'success',
});

const STATUS_CONDITION_KEYS = ['status_2d', 'status_3d'];

export const formatSendDateTime = (value) => (value ? dayjs(value).format('DD.MM.YYYY HH:mm') : '');

export const isSentState = (state) => [SEND_STATE.SENT, SEND_STATE.PARTIALLY_SENT].includes(state);

/**
 * Цвет тега определяет только покрытие: сколько вебхуков заказчика закрыто.
 * Ручная отправка на цвет не влияет — она видна в тексте.
 */
export const sendStateColor = (state) => STATE_COLORS[state] ?? 'default';

export const hasPendingResult = (status) => (status?.webhooks ?? [])
  .some((webhook) => !webhook.sent && webhook.sent_at && webhook.supports_strategy);

const sourceLabel = (source, initiatorName) => (source === 'manual'
  ? `вручную${initiatorName ? `, ${initiatorName}` : ''}`
  : 'автоматически');

export const sendStateText = (status) => {
  if (!status) return '';

  const suffix = status.last_send?.source === 'manual' ? ' вручную' : '';
  const pendingResult = hasPendingResult(status);

  switch (status.state) {
    case SEND_STATE.NOT_CONFIGURED:
      return 'Отправка не настроена';
    case SEND_STATE.SENT:
      return `Отправлено${suffix}`;
    case SEND_STATE.PARTIALLY_SENT:
      return `Отправлено${suffix} частично (${status.sent_count} из ${status.target_count})`;
    default:
      return pendingResult ? 'Обновлённый результат не отправлен' : 'Не отправлено';
  }
};

export const conditionsTitle = (status) => (status?.conditions_as_of
  ? `Состояние на момент отправки ${formatSendDateTime(status.conditions_as_of)}`
  : 'Текущее состояние');

export const lastSendNote = (status) => {
  const lastSend = status?.last_send;

  if (!lastSend) return null;

  const who = sourceLabel(lastSend.source, lastSend.initiator_name);
  const note = `Последняя отправка: ${formatSendDateTime(lastSend.sent_at)}, ${who}`;

  return lastSend.has_snapshot
    ? note
    : `${note}. Состояния на момент отправки не сохранилось — ниже текущее.`;
};

/**
 * Статусы приходят числами: имена знает только фронт, поэтому разворачиваем их здесь.
 */
export const conditionDetail = (condition) => {
  if (!STATUS_CONDITION_KEYS.includes(condition.key)) return condition.detail ?? condition.failure;

  const current = getStatusTextByNum(condition.meta?.status) ?? condition.meta?.status;
  const allowed = (condition.meta?.allowed ?? []).map((value) => getStatusTextByNum(value) ?? value);

  return allowed.length
    ? `${current}; у заказчика: ${allowed.join(', ')}`
    : `${current}; у заказчика список не задан`;
};

export const webhookDetail = (webhook) => {
  if (!webhook.sent) {
    if (webhook.dead_lettered) return 'доставка провалена';

    if (!webhook.supports_strategy) return 'нет шаблона под текущую стратегию';
    return webhook.sent_at ? 'обновлённый результат не отправлен' : 'не отправлено';
  }

  return `${formatSendDateTime(webhook.sent_at)}, ${sourceLabel(webhook.source, webhook.initiator_name)}`;
};
